/**
 * The page's one way of asking whether the StockMaid extension is installed.
 * Pattern ported from onetapapply's extensionBridge (same owner, same rules).
 *
 * chrome.runtime is only injected into pages whose origin the extension names
 * in externally_connectable, and sendMessage to an id that is not installed
 * fails through lastError rather than throwing — so "not installed", "wrong
 * id", and "different extension" all arrive as the same quiet null. That is
 * why probeExtension returns null ("cannot tell") everywhere the question
 * could not have been heard, and a real {installed:false} only where absence
 * is evidence: our own origins, in a Chromium browser, with an id configured.
 */

// The published Chrome Web Store id is stable and public; the env var exists
// for dev builds addressing an unpacked copy.
const DEFAULT_EXTENSION_ID = 'eagpnnacaeonojmmkgnmnbgddfikfpfk';

const extensionId = () => process.env.REACT_APP_EXTENSION_ID || DEFAULT_EXTENSION_ID;

export const EXTENSION_STORE_URL =
    `https://chromewebstore.google.com/detail/${DEFAULT_EXTENSION_ID}`;

/** The status handshake: any answer means installed. */
export const EXTENSION_STATUS_MESSAGE = { type: 'STOCKMAID_STATUS_V1' };
/** The connect handshake: the page hands over its token, the extension verifies it right then. */
export const EXTENSION_CONNECT_MESSAGE_TYPE = 'STOCKMAID_CONNECT_V1';
/** Refresh the watchlist now (1.5.0+); a successful poll is what clears the extension's "rejected" flag. */
export const EXTENSION_POLL_NOW_MESSAGE = { type: 'STOCKMAID_POLL_NOW_V1' };

/**
 * The origins the extension names in externally_connectable. The bridge test
 * reads the extension manifest and fails if these lists stop agreeing.
 */
export const CONNECTABLE_ORIGINS = ['https://stockmaid.com', 'https://www.stockmaid.com'];

export const canAskExtension = () => Boolean(
    extensionId()
    && typeof window !== 'undefined'
    && window.chrome
    && window.chrome.runtime
    && window.chrome.runtime.sendMessage
);

/** Never rejects: a failure and an uninstalled extension call for the same response. */
export const askExtension = (message) => new Promise((resolve) => {
    if (!canAskExtension()) {
        resolve(null);
        return;
    }
    try {
        window.chrome.runtime.sendMessage(extensionId(), message, (response) => {
            if (window.chrome.runtime.lastError) {
                resolve(null);
                return;
            }
            resolve(response || null);
        });
    } catch (e) {
        resolve(null);
    }
});

/**
 * userAgentData rather than the UA string: this decides whether somebody is
 * offered a Chrome extension, and the UA string is the field everybody lies
 * in. Anything that does not answer is treated as not Chromium, which only
 * ever means one prompt fewer.
 */
const isChromium = () => Boolean(
    typeof navigator !== 'undefined'
    && navigator.userAgentData
    && navigator.userAgentData.brands
    && navigator.userAgentData.brands.some((entry) => entry.brand === 'Chromium')
);

export const absenceIsObservable = (origin) => Boolean(
    extensionId() && isChromium() && CONNECTABLE_ORIGINS.includes(origin)
);

/** {installed, connected}, or null when the question cannot honestly be answered here. */
export const probeExtension = async () => {
    if (canAskExtension()) {
        const response = await askExtension(EXTENSION_STATUS_MESSAGE);
        return { installed: Boolean(response), connected: Boolean(response && response.connected) };
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return absenceIsObservable(origin) ? { installed: false, connected: false } : null;
};

const settle = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Connects the installed extension to this signed-in session, onetapapply's
 * pattern (#234). Resolves {ok, connected, reason}; never rejects.
 *
 * The extension verifies the token against the API before answering, so an
 * `ok` here means tracking can go ahead right now, and `reason: 'rejected'`
 * means the API refused the page's own token — the one case where sending
 * somebody back through sign-in is the fix rather than a loop.
 *
 * Builds that predate CONNECT (1.5.0) decline unknown messages, which arrives
 * here as null. Those builds have already picked the token up from
 * localStorage through auth_sync.js; what they lack is a reason to re-check
 * it, so ask for a poll (that clears their rejected flag on success) and read
 * STATUS again once it has had time to land.
 */
export const connectExtension = async ({ settleMs = 2500 } = {}) => {
    let token = '';
    try { token = window.localStorage.getItem('authToken') || ''; } catch (e) { token = ''; }
    if (!token) {
        return { ok: false, connected: false, reason: 'signed_out' };
    }

    const reply = await askExtension({ type: EXTENSION_CONNECT_MESSAGE_TYPE, token });
    if (reply && typeof reply.ok === 'boolean') {
        return {
            ok: reply.ok,
            connected: Boolean(reply.connected),
            reason: reply.ok ? null : (reply.reason || 'failed')
        };
    }

    const polled = await askExtension(EXTENSION_POLL_NOW_MESSAGE);
    if (!polled) {
        return { ok: false, connected: false, reason: 'unreachable' };
    }
    await settle(settleMs);
    const status = await probeExtension();
    if (status && status.connected) {
        return { ok: true, connected: true, reason: null };
    }
    return { ok: false, connected: false, reason: 'rejected' };
};
